import React, { useEffect, useMemo, useRef, useState } from "react";
import DrawingSheet from "./DrawingSheet";
import SheetToolbar from "./SheetToolbar";
import useDrawingSheetState from "../../hooks/useDrawingSheetState";
import useActiveViewport from "../../hooks/useActiveViewport";
import { createDrawingView, createSectionView } from "../../schema/drawingView.schema";
import { createSheetViewport } from "../../schema/drawingSheet.schema";
import { createOverallDimensionsForView } from "../../engine/dimensionAutoBuilder";
import { normalizeViewportToMm } from "../../engine/sheetLayoutUtils";
import { resolveNodeTree } from "../../../engine/resolveNodeTree";
import { getViewAxes } from "../../engine/viewProjection";
import {
  getDimensionOrientationFromRefs,
  getDimensionValueFromRefs,
} from "../../engine/dimensionReferenceUtils";

export default function DrawingSheetPage({
  rootInstances,
  definitions,
  globalParameters,
  room,
  selectedRoomObjectId,
  onSelectRoomObject,
  selectedId,
  selectedIds,
  onMultiSelect,
  onSelect,
  onResolvedSelect,
  onDoubleOpen,
  onTransformNode,
  onChangeNode,
  materials,
  documentData,
  onDocumentDataChange,
}) {
  const {
  document,
  updateDocument,
  activeSheet,
  updateView,
  updateViewport,
  moveSheetViewport,
  resizeSheetViewport,
  deleteViewAndViewports,
  replaceViewportAnnotations,
  addAnnotation,
  updateAnnotation,
  deleteAnnotation,
  emitDocumentChange,
} = useDrawingSheetState(documentData, onDocumentDataChange);

const [drawingTool, setDrawingTool] = useState("select");
const [dimensionDraft, setDimensionDraft] = useState(null);
const [selectedAnnotationId, setSelectedAnnotationId] = useState(null);

  const {
    activeViewportId,
    activateViewport,
    deactivateViewport,
  } = useActiveViewport();
  const [selectedViewportId, setSelectedViewportId] = useState(null);

  function handleSelectAnnotation(annotationId) {
  setSelectedAnnotationId(annotationId);
}



function isZeroOrigin(origin) {
  return (
    !origin ||
    (Number(origin.x || 0) === 0 &&
      Number(origin.y || 0) === 0 &&
      Number(origin.z || 0) === 0)
  );
}

function is2DDocumentationView(view) {
  return !(
    view?.type === "iso" ||
    view?.cameraType === "perspective"
  );
}

function handleFitSelectedViewProjection() {
  if (!selectedView?.id) return;

  const box = getResolvedWorldBox(resolvedForDimensions);

  if (!box) {
    alert("Fit View: no valid cabinet box geometry found.");
    return;
  }

  const center = getWorldBoxCenter(box);

  updateView(selectedView.id, (view) => ({
    projection: {
      ...(view.projection || {}),
      originWorld: center,
    },
    camera: null,
  }));
}

function handleDeleteSelectedAnnotation() {
  if (!activeSheet?.id || !selectedAnnotationId) return;

  deleteAnnotation(activeSheet.id, selectedAnnotationId);
  setSelectedAnnotationId(null);
}

function handleUpdateAnnotation(annotationId, patch) {
  if (!activeSheet?.id || !annotationId) return;

  updateAnnotation(activeSheet.id, annotationId, patch);
}

function handleSelectViewport(viewportId) {
  setSelectedViewportId(viewportId);
}
  
  const [sheetZoom, setSheetZoom] = useState(1);

  const selectedViewport = activeSheet?.viewports?.find(
  (vp) => vp.id === selectedViewportId
);
const workspaceRef = useRef(null);

const selectedView = selectedViewport
  ? document.views.find((view) => view.id === selectedViewport.viewId)
  : null;

const resolvedForDimensions = useMemo(() => {
  return resolveNodeTree({
    rootInstances,
    definitions,
    globalParameters,
  });
}, [rootInstances, definitions, globalParameters]);

useEffect(() => {
  if (!document?.views?.length) return;
  if (!resolvedForDimensions?.length) return;

  const box = getResolvedWorldBox(resolvedForDimensions);
  if (!box) return;

  const center = getWorldBoxCenter(box);

  let changed = false;

  const nextViews = document.views.map((view) => {
    if (!is2DDocumentationView(view)) return view;

    const currentOrigin = view?.projection?.originWorld;

    // Bitno: menjamo samo views koji su još na default 0,0,0.
    // Ako je korisnik već ručno pomerio/fitovao pogled, ne diramo ga.
    if (!isZeroOrigin(currentOrigin)) return view;

    changed = true;

    return {
      ...view,
      projection: {
        ...(view.projection || {}),
        originWorld: center,
      },
      camera: null,
    };
  });

  if (!changed) return;

  updateDocument((prev) => ({
    ...prev,
    views: nextViews,
  }));
}, [
  document?.views?.length,
  resolvedForDimensions,
]);

function updateSelectedViewport(patch) {
  if (!activeSheet?.id || !selectedViewport?.id) return;

  const normalized = normalizeViewportToMm(selectedViewport);

  updateViewport(activeSheet.id, selectedViewport.id, {
    ...normalized,
    ...patch,
  });
}

function handleDimensionPick({ viewport, view, candidate }) {
  if (!activeSheet || !viewport || !view || !candidate) return;

  if (!dimensionDraft?.ref1) {
    setDimensionDraft({
      viewportId: viewport.id,
      viewId: view.id,
      ref1: candidate,
    });
    return;
  }

  if (
    dimensionDraft.viewportId !== viewport.id ||
    dimensionDraft.viewId !== view.id
  ) {
    setDimensionDraft({
      viewportId: viewport.id,
      viewId: view.id,
      ref1: candidate,
    });
    return;
  }

  const ref1 = dimensionDraft.ref1;
  const ref2 = candidate;

  if (
  ref1.dimensionOrientation &&
  ref2.dimensionOrientation &&
  ref1.dimensionOrientation !== ref2.dimensionOrientation
) {
  alert("Selected references are not aligned for the same dimension direction.");
  setDimensionDraft(null);
  return;
}

const orientation =
  ref1.dimensionOrientation ||
  ref2.dimensionOrientation ||
  getDimensionOrientationFromRefs(ref1, ref2, view);
const value = Math.round(
  getDimensionValueFromRefs(ref1, ref2, view, orientation)
);

  const annotation = {
    id: `dim_ref_${viewport.id}_${Date.now()}`,
    type: "dimension",
    mode: "manual_ref",
    viewportId: viewport.id,
    viewId: view.id,
    orientation,
    value,
    text: `${value} mm`,
    ref1,
    ref2,
    offsetMm: 10,
  };

  addAnnotation(activeSheet.id, annotation);
  setDimensionDraft(null);
  setDrawingTool("select");
}

function handleDeleteSelectedView() {
  if (!selectedView?.id) return;

  const ok = window.confirm(
    `Delete ${selectedView.name || selectedViewport?.title || "selected view"}?`
  );

  if (!ok) return;

  deleteViewAndViewports?.(selectedView.id);

  setSelectedViewportId(null);
  deactivateViewport?.();
}

function flattenResolvedNodes(nodes = [], acc = []) {
  (nodes || []).forEach((node) => {
    acc.push(node);
    flattenResolvedNodes(node.children || [], acc);
  });

  return acc;
}

function getNodeWorldBoxForProjection(node) {
  const p = node?.worldTransform?.position || {};
  const g = node?.geometry || {};
  const ep = node?.effectiveParameters || {};
  const pp = node?.parameters || node?.parameterValues || {};

  const width = Number(g.width ?? ep.width ?? pp.width ?? 0);
  const height = Number(g.height ?? ep.height ?? pp.height ?? 0);
  const depth = Number(g.depth ?? ep.depth ?? pp.depth ?? 0);

  const cx = Number(p.x || 0);
  const cy = Number(p.y || 0);
  const cz = Number(p.z || 0);

  if (!width && !height && !depth) return null;

  return {
    min: {
      x: cx - width / 2,
      y: cy - height / 2,
      z: cz - depth / 2,
    },
    max: {
      x: cx + width / 2,
      y: cy + height / 2,
      z: cz + depth / 2,
    },
  };
}

function getResolvedWorldBox(nodes = []) {
  let box = null;

  flattenResolvedNodes(nodes).forEach((node) => {
    const category = String(
      node?.category ||
      node?.definition?.category ||
      ""
    ).toLowerCase();

    // Za dokumentaciju prvo centriramo po cabinet.box.
    // Kasnije možemo dodati Fit All / Fit Selected opcije.
    if (!category.includes("cabinet.box")) return;

    const b = getNodeWorldBoxForProjection(node);
    if (!b) return;

    if (!box) {
      box = {
        min: { ...b.min },
        max: { ...b.max },
      };
      return;
    }

    box.min.x = Math.min(box.min.x, b.min.x);
    box.min.y = Math.min(box.min.y, b.min.y);
    box.min.z = Math.min(box.min.z, b.min.z);

    box.max.x = Math.max(box.max.x, b.max.x);
    box.max.y = Math.max(box.max.y, b.max.y);
    box.max.z = Math.max(box.max.z, b.max.z);
  });

  return box;
}

function getWorldBoxCenter(box) {
  if (!box) return { x: 0, y: 0, z: 0 };

  return {
    x: Number(((box.min.x + box.max.x) / 2).toFixed(3)),
    y: Number(((box.min.y + box.max.y) / 2).toFixed(3)),
    z: Number(((box.min.z + box.max.z) / 2).toFixed(3)),
  };
}

function handleAutoDimensions() {
  if (!activeSheet || !selectedViewport || !selectedView) return;

  const dimensions = createOverallDimensionsForView({
    view: selectedView,
    viewport: selectedViewport,
    resolvedNodes: resolvedForDimensions,
  });

  console.log("AUTO DIMENSIONS", {
    selectedView,
    selectedViewport,
    resolvedCount: resolvedForDimensions?.length,
    dimensions,
  });

  if (!dimensions.length) {
    alert("Auto dimensions: no valid geometry found for this view.");
    return;
  }

  replaceViewportAnnotations(
    activeSheet.id,
    selectedViewport.id,
    dimensions
  );
}

function fitSheetToScreen() {
  const el = workspaceRef.current;
  if (!el || !activeSheet?.paper) return;

  const paper = activeSheet.paper;
  const basePxPerMm = 3;

  const availableWidth = el.clientWidth - 64;
  const availableHeight = el.clientHeight - 64;

  const sheetWidthPx = paper.widthMm * basePxPerMm;
  const sheetHeightPx = paper.heightMm * basePxPerMm;

  const zoom = Math.min(
    availableWidth / sheetWidthPx,
    availableHeight / sheetHeightPx
  );

  setSheetZoom(Math.max(0.2, Math.min(2, zoom)));
}

// useEffect(() => {
//   if (!activeSheet?.paper) return;

//   const timer = window.setTimeout(() => {
//     fitSheetToScreen();
//   }, 50);

//   return () => window.clearTimeout(timer);
// }, [activeSheet?.id]);

useEffect(() => {
  const el = workspaceRef.current;
  if (!el) return;

  function onNativeWheel(e) {
    if (!e.shiftKey) return;

    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation?.();

    const rect = el.getBoundingClientRect();

    const mouseX = e.clientX - rect.left + el.scrollLeft;
    const mouseY = e.clientY - rect.top + el.scrollTop;

    setSheetZoom((prev) => {
      const prevZoom = Number(prev || 1);
      const zoomFactor = e.deltaY < 0 ? 1.12 : 1 / 1.12;

      const nextZoom = Math.max(
        0.15,
        Math.min(10, prevZoom * zoomFactor)
      );

      if (Math.abs(nextZoom - prevZoom) < 0.001) return prevZoom;

      const ratio = nextZoom / prevZoom;

      requestAnimationFrame(() => {
        el.scrollLeft = mouseX * ratio - (e.clientX - rect.left);
        el.scrollTop = mouseY * ratio - (e.clientY - rect.top);
      });

      return nextZoom;
    });
  }

  el.addEventListener("wheel", onNativeWheel, {
    capture: true,
    passive: false,
  });

  return () => {
    el.removeEventListener("wheel", onNativeWheel, {
      capture: true,
    });
  };
}, []);

function updateSelectedView(patch) {
  if (!selectedView?.id) return;

  updateView(selectedView.id, patch);
}

function alignSelectedViewport(mode) {
  if (!activeSheet || !selectedViewport) return;

  const paper = activeSheet.paper || { widthMm: 420, heightMm: 297 };
  const marginMm = 15;

  const widthMm = Number(selectedViewport.widthMm ?? selectedViewport.width ?? 100);
  const heightMm = Number(selectedViewport.heightMm ?? selectedViewport.height ?? 80);

  if (mode === "left") {
    updateSelectedViewport({ xMm: marginMm });
  }

  if (mode === "right") {
    updateSelectedViewport({
      xMm: paper.widthMm - widthMm - marginMm,
    });
  }

  if (mode === "top") {
    updateSelectedViewport({ yMm: marginMm });
  }

  if (mode === "bottom") {
    updateSelectedViewport({
      yMm: paper.heightMm - heightMm - marginMm,
    });
  }

  if (mode === "centerH") {
    updateSelectedViewport({
      xMm: Math.round((paper.widthMm - widthMm) / 2),
    });
  }

  if (mode === "centerV") {
    updateSelectedViewport({
      yMm: Math.round((paper.heightMm - heightMm) / 2),
    });
  }
}

  function addViewAndViewport(view, viewportPatch = {}) {
    updateDocument((prev) => {
      const sheet = prev.sheets.find((s) => s.id === prev.activeSheetId);
      if (!sheet) return prev;

      const viewport = createSheetViewport({
  viewId: view.id,
  title: view.name,
  xMm: 20,
  yMm: 20,
  widthMm: 160,
  heightMm: 90,
  ...viewportPatch,
});

      return {
        ...prev,
        views: [...prev.views, view],
        sheets: prev.sheets.map((s) =>
          s.id === sheet.id
            ? {
                ...s,
                viewports: [...s.viewports, viewport],
              }
            : s
        ),
      };
    });
  }

  function handleAddFrontView() {
    addViewAndViewport(
      createDrawingView({
        name: "Front View",
        type: "front",
        cameraType: "orthographic",
        scale: 20,
      }),
      {
        title: "FRONT VIEW",
      }
    );
  }

  function handleAddTopView() {
    addViewAndViewport(
      createDrawingView({
        name: "Top View",
        type: "top",
        cameraType: "orthographic",
        scale: 20,
      }),
      {
        title: "TOP VIEW",
      }
    );
  }

  function getNextSectionLabel() {
  const existing = (document.views || []).filter((v) => v.type === "section");
  const index = existing.length;

  const letter = String.fromCharCode("A".charCodeAt(0) + index);

  return `${letter}-${letter}`;
}

  function handleAddSection() {
  const sectionId = `section_${Date.now()}`;
  const markerId = `marker_${Date.now()}`;
  const label = getNextSectionLabel();

  const sectionView = createSectionView({
    id: sectionId,
    name: `Section ${label}`,
    sourceViewId: "view_top",
    label,
    start: { x: -500, z: 0 },
    end: { x: 500, z: 0 },
    axis: "z",
    value: 0,
    direction: "front",
    depth: 1000,
    scale: 20,
    angleDeg: 0,
  });

  sectionView.sectionPlane = {
    ...(sectionView.sectionPlane || {}),
    markerId,
  };

  addViewAndViewport(sectionView, {
    title: `SECTION ${label}`,
    xMm: 250,
    yMm: 60,
    widthMm: 120,
    heightMm: 90,
  });
}

function handleCreateSectionFromView({ sourceView, worldPoint }) {
  if (!sourceView || sourceView.type !== "top") return;

  const z = Number(worldPoint?.z || 0);
  const x = Number(worldPoint?.x || 0);

  const sectionId = `section_${Date.now()}`;
  const markerId = `marker_${Date.now()}`;
  const label = getNextSectionLabel();

  const sectionView = createSectionView({
    id: sectionId,
    name: `Section ${label}`,
    sourceViewId: sourceView.id,
    label,

    start: { x: x - 500, z },
    end: { x: x + 500, z },

    axis: "z",
    value: z,
    direction: "front",
    depth: 1000,
    scale: sourceView.scale || 20,
    angleDeg: 0,
  });

  sectionView.projection = {
  ...(sectionView.projection || {}),
  originWorld: {
    x,
    y: sourceView?.projection?.originWorld?.y || 0,
    z,
  },
};

  sectionView.sectionPlane = {
    ...(sectionView.sectionPlane || {}),
    markerId,
  };

  addViewAndViewport(sectionView, {
    title: `SECTION ${label}`,
    xMm: 250,
    yMm: 60,
    widthMm: 120,
    heightMm: 90,
  });

  setDrawingTool("select");
}

  return (
    <div className="w-full h-full bg-slate-100 flex flex-col overflow-hidden">
      <SheetToolbar
  activeViewportId={activeViewportId}
  selectedViewport={selectedViewport}
  selectedView={selectedView}
  sheetZoom={sheetZoom}
  onSheetZoomChange={setSheetZoom}
  onDeactivateViewport={deactivateViewport}
  onAddFrontView={handleAddFrontView}
  onAddTopView={handleAddTopView}
  onAddSection={handleAddSection}
  onUpdateSelectedViewport={updateSelectedViewport}
  onUpdateSelectedView={updateSelectedView}
  onAlignSelectedViewport={alignSelectedViewport}
  onFitSheet={fitSheetToScreen}
  onFitSelectedView={handleFitSelectedViewProjection}
  onAutoDimensions={handleAutoDimensions}
  drawingTool={drawingTool}
  onDrawingToolChange={setDrawingTool}
  onDeleteSelectedView={handleDeleteSelectedView}
  selectedAnnotationId={selectedAnnotationId}
  onDeleteSelectedAnnotation={handleDeleteSelectedAnnotation}
  onSaveDrawing={emitDocumentChange}
/>

      <div
  ref={workspaceRef}
  className="flex-1 overflow-auto p-8"
>
        <DrawingSheet
  sheet={activeSheet}
  views={document.views}
  sheetZoom={sheetZoom}
  drawingTool={drawingTool}
  onCreateSectionFromView={handleCreateSectionFromView}
  activeViewportId={activeViewportId}
  selectedViewportId={selectedViewportId}
  onSelectViewport={handleSelectViewport}
  onMoveViewport={moveSheetViewport}
  onResizeViewport={resizeSheetViewport}
  onUpdateViewport={updateViewport}
  onUpdateView={updateView}
  onActivateViewport={activateViewport}
  onDeactivateViewport={deactivateViewport}
  rootInstances={rootInstances}
  definitions={definitions}
  globalParameters={globalParameters}
  room={room}
  selectedRoomObjectId={selectedRoomObjectId}
  onSelectRoomObject={onSelectRoomObject}
  selectedId={selectedId}
  selectedIds={selectedIds}
  onMultiSelect={onMultiSelect}
  onSelect={onSelect}
  onResolvedSelect={onResolvedSelect}
  onDoubleOpen={onDoubleOpen}
  onTransformNode={onTransformNode}
  onChangeNode={onChangeNode}
  materials={materials}
  drawingTool={drawingTool}
dimensionDraft={dimensionDraft}
onDimensionPick={handleDimensionPick}
selectedAnnotationId={selectedAnnotationId}
onSelectAnnotation={handleSelectAnnotation}
onUpdateAnnotation={handleUpdateAnnotation}
onDeleteSelectedAnnotation={handleDeleteSelectedAnnotation}
/>
      </div>
    </div>
  );
}